import { Box, ButtonBase, Typography } from "@material-ui/core";
import clsx from "clsx";
import React from "react";
import { PagedContentCarousel } from "halifax";
import { useHistory } from "react-router";
import {
  CLICKED_CURATED_HOTEL,
  DatelessSearchSource,
  transformToShopDetailsStringifiedQuery,
  VibeSecondaryLodging,
  VIEWED_CURATED_HOTEL,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { PATH_SHOP_DETAILS } from "../../../../utils/paths";
import { WishlistToggleHeart } from "../../../wishlist";
import styles from "./styles.module.scss";

export interface SecondaryPropertiesSectionProps {
  properties?: VibeSecondaryLodging[];
  title?: string;
  vibeName: string;
  isMobile: boolean;
  isWishlistExperiment: boolean;
}

export const SecondaryPropertiesSection: React.FC<SecondaryPropertiesSectionProps> = ({
  properties = [],
  title,
  vibeName,
  isMobile,
  isWishlistExperiment,
}) => {
  const itemsPerPage = isMobile ? 1 : 3;
  const history = useHistory();
  const images = React.useMemo(
    () =>
      properties.flatMap((property) =>
        (property.media ?? [])
          .filter((media) => media.type === "image")
          .map((media, index) => ({
            key: `${property.lodgingId}-${index}`,
            source: media.source,
            name: property.name,
            location: property.location,
            lodgingId: property.lodgingId,
            lodgingCollection: property.lodgingCollection,
          }))
      ),
    [properties]
  );
  const pagedItems = React.useMemo(() => {
    if (images.length <= itemsPerPage) {
      return images.length > 0 ? [images] : [];
    }

    return Array.from({ length: images.length }, (_, pageIndex) =>
      Array.from({ length: itemsPerPage }, (_, offset) => {
        const itemIndex = (pageIndex + offset) % images.length;
        return images[itemIndex];
      })
    );
  }, [images, itemsPerPage]);
  const pageCount = pagedItems.length;
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const prevIndexRef = React.useRef<number | null>(null);
  const [loadedSources, setLoadedSources] = React.useState<
    Record<string, boolean>
  >({});
  const navigateToLodging = React.useCallback(
    (lodgingId: string, propertyName: string) => {
      trackEvent({
        eventName: CLICKED_CURATED_HOTEL,
        properties: {
          source: "vibes_secondary",
          vibe_name: vibeName,
          hotel_id: lodgingId,
          hotel_name: propertyName,
        },
      });
      const shopParams = transformToShopDetailsStringifiedQuery({
        lodgingId,
        source: DatelessSearchSource.Vibes,
        vibe: vibeName,
      });
      const url = history.createHref({
        pathname: PATH_SHOP_DETAILS,
        search: shopParams,
      });
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [history, vibeName]
  );

  React.useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, Math.max(pageCount - 1, 0)));
  }, [pageCount]);

  React.useEffect(() => {
    const pageItems = pagedItems[currentIndex] ?? [];
    if (!vibeName || pageItems.length === 0) {
      return;
    }
    const prevIndex = prevIndexRef.current;
    prevIndexRef.current = currentIndex;

    const itemsToTrack =
      prevIndex === null
        ? pageItems
        : pageItems.filter(
            (item) =>
              !(pagedItems[prevIndex] ?? []).some(
                (prev) => prev.key === item.key
              )
          );

    for (const item of itemsToTrack) {
      const hotelIndex = properties.findIndex((p) => p.lodgingId === item.lodgingId);
      trackEvent({
        eventName: VIEWED_CURATED_HOTEL,
        properties: {
          hotel_name: item.name,
          lodging_id: item.lodgingId,
          hotel_index: hotelIndex >= 0 ? hotelIndex : undefined,
          section: "secondary",
          vibe_name: vibeName,
        },
      });
    }
  }, [currentIndex, pagedItems, vibeName, properties]);

  const markLoaded = React.useCallback((source: string) => {
    setLoadedSources((prev) =>
      prev[source] ? prev : { ...prev, [source]: true }
    );
  }, []);

  React.useEffect(() => {
    if (pageCount === 0) {
      return;
    }

    let cancelled = false;
    const preloadPages = [currentIndex, currentIndex - 1, currentIndex + 1]
      .filter((index) => index >= 0 && index < pageCount)
      .flatMap((index) => pagedItems[index] ?? []);

    preloadPages.forEach((item) => {
      if (!item.source || loadedSources[item.source]) {
        return;
      }

      const image = new Image();
      image.onload = () => {
        if (!cancelled) {
          markLoaded(item.source);
        }
      };
      image.onerror = () => {
        if (!cancelled) {
          markLoaded(item.source);
        }
      };
      image.src = item.source;
    });

    return () => {
      cancelled = true;
    };
  }, [currentIndex, pageCount, pagedItems, loadedSources, markLoaded]);

  const pages = pagedItems.map((pageItems, pageIndex) => (
    <Box className={styles.page} key={`page-${pageIndex}`}>
      {pageItems.map((item) => (
        <ButtonBase
          className={styles.card}
          key={item.key}
          onClick={() => navigateToLodging(item.lodgingId, item.name)}
        >
          {isWishlistExperiment && (
            <WishlistToggleHeart
              listingId={item.lodgingId}
              listingImageUrl={item.source}
              stayType={StayType.Hotels}
              collection={item.lodgingCollection}
              style={{
                position: "absolute",
                top: isMobile ? 10 : 24,
                right: isMobile ? 10 : 24,
                zIndex: 1,
              }}
            />
          )}
          <img
            className={clsx(styles.cardImage, {
              [styles.loaded]: loadedSources[item.source],
            })}
            src={item.source}
            alt={`${item.name} in ${item.location}`}
            onLoad={() => markLoaded(item.source)}
            onError={() => markLoaded(item.source)}
          />
          <Box className={styles.labelContainer}>
            <Box className={styles.label}>
              <Typography className={styles.labelText} variant="caption">
                {item.name} | {item.location}
              </Typography>
            </Box>
          </Box>
        </ButtonBase>
      ))}
    </Box>
  ));

  return (
    <Box
      className={clsx(styles.root, {
        [styles.mobile]: isMobile,
      })}
    >
      <Typography className={styles.title} variant="h4">
        {title}
      </Typography>
      <PagedContentCarousel
        className={styles.carousel}
        pages={pages}
        isMobile={isMobile}
        onIndexChange={setCurrentIndex}
      />
    </Box>
  );
};
